import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { formatAddressModel, workCodeLabel } from '../pace-spector.utils';
import { TranslateModule } from '@ngx-translate/core';
import { WorkentryNgrxSpectorComponent } from '../workentry-ngrx-spector/workentry-ngrx-spector.component';
import {
    AddressState,
    NotificationIdState,
    NotificationState,
    NotificationStateUtilities,
    TimesheetManualEntryState,
    TimesheetState,
    TimesheetWorkEntryState,
} from '../../../state/';

@Component({
    selector: 'pace-core-timesheet-spector-ngrx',
    imports: [
        CommonModule,
        AlertComponent,
        AccordionComponent,
        AccordionPanelComponent,
        TranslateModule,
        WorkentryNgrxSpectorComponent,
    ],
    templateUrl: './timesheet-spector-ngrx.component.html',
    styleUrl: './timesheet-spector-ngrx.component.css',
})
export class TimesheetSpectorNgrxComponent {
    public model = input.required<TimesheetState>();
    public addressesById = input.required<Map<string, AddressState>>();
    public notifications = input.required<NotificationState[]>();

    public hasData = computed(() => !!this.model());
    public workEntriesByNotification = computed<Map<NotificationIdState, TimesheetWorkEntryState[]>>(() => {
        const workEntries = this.model()?.entries.filter((entry) => entry.kind === 'work') ?? [];
        const byNotification = new Map<NotificationIdState, TimesheetWorkEntryState[]>();

        for (const item of workEntries) {
            const bucket = byNotification.get(item.notificationId);
            if (bucket) {
                bucket.push(item);
            } else {
                byNotification.set(item.notificationId, [item]);
            }
        }

        return byNotification;
    });
    public manualEntries = computed<TimesheetManualEntryState[]>(
        () => this.model()?.entries.filter((entry) => entry.kind === 'manual') ?? [],
    );

    public modelDisplay = computed(() => {
        const model = this.model();

        return {
            startOfWork: this.fmtDateTime(model?.startOfWork),
            endOfWork: this.fmtDateTime(model?.endOfWork),
            isSubmitted: model?.isSubmitted ?? false,
            attendanceType: model?.attendanceType ?? '--',
            startAddressValue: model?.startAddressValue ?? '--',
            endAddressValue: model?.endAddressValue ?? '--',
            saveTimestamp: this.fmtEpoch(model?.saveTimestamp),
        };
    });

    public summaryCountText = computed(() => {
        const n = this.workEntriesByNotification().size;
        const m = this.manualEntries().length;
        return `${n} notif // ${m} manual`;
    });

    public deleteTimesheet = output<void>();
    public downloadJson = output<TimesheetState>();

    public copyJson(): void {
        const raw = this.model();
        if (!raw) {
            return;
        }
        const text = JSON.stringify(raw, null, 2);
        navigator.clipboard?.writeText(text).catch(console.error);
    }

    public triggerDownloadJson(): void {
        const model = this.model();
        if (!model) {
            return;
        }
        this.downloadJson.emit(model);
    }

    protected fmtDateTime(v?: string | null): string {
        if (!v) {
            return '--';
        }
        const d = new Date(v);
        return isNaN(d.getTime()) ? v : d.toLocaleString();
    }

    private fmtEpoch(v?: number | null): string {
        if (v === null || v === undefined) {
            return '--';
        }
        const d = new Date(v);
        return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
    }

    protected formatAddress(a?: string | null): string {
        if (!a) {
            return 'undefined address';
        }
        const address = this.addressesById()?.get(a);
        return formatAddressModel(address);
    }

    protected readonly workCodeLabel = workCodeLabel;

    public triggerDelete(): void {
        this.deleteTimesheet.emit();
    }

    protected findNotification(key: NotificationIdState): NotificationState | undefined {
        const notifications = this.notifications();
        return notifications.find((n) => NotificationStateUtilities.compare(n.notificationId, key));
    }
}
